import { isEqual } from 'lodash';
import { useCallback } from 'react';

import {
  Appointment,
  BundleBasketFragment,
  OccupancyType,
} from '@generated/gql/graphql';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { UserInformation } from '@src/types/auth';
import { useTrackingContext } from '@tracking/Provider/Context';
import { SignupApplicationEntity } from '@tracking/trackingEvents';
import { parseSessionContext } from '@tracking/utils/parseAttributionContext';
import { formatInsurance } from '@utils/formatBasket/insurance';

const getApplicationTrackingContext = (
  basket: BundleBasketFragment,
  userInformation: Nullable<UserInformation>,
  appointment: Nullable<Appointment>,
  isHostedLink?: boolean
): SignupApplicationEntity => {
  const {
    details: { occupancyType, createdAt },
  } = basket;

  const { boilerInsurance, incomeProtector } = formatInsurance(basket);

  const sessionContext = parseSessionContext(appointment, userInformation);

  return {
    created_at: createdAt ?? '',
    basket_id: basket.id,
    supply_postcode: basket.details?.supplyAddress?.postcode,
    is_home_owner: occupancyType === OccupancyType.Homeowner,
    is_hosted_link: isHostedLink,
    ...sessionContext,
    is_boiler_home_cover_available: boilerInsurance.available,
    is_income_protector_available: incomeProtector.available,
    is_property_already_supplied: basket.isPropertyAlreadySupplied ?? undefined,
  };
};

const useApplicationEntityUpdate = () => {
  const userInformation = useUserInformation();
  const { setApplicationTrackingContext, applicationTrackingContext } =
    useTrackingContext();
  const { appointment } = useAppointmentContext();
  const { hostedId } = useHostedAttribution();
  const isHostedLink = Boolean(hostedId);

  const updateApplicationContext = useCallback(
    (basket: Nullable<BundleBasketFragment>) => {
      if (!basket) {
        return;
      }

      const updatedApplicationContext = getApplicationTrackingContext(
        basket,
        userInformation,
        appointment,
        isHostedLink
      );

      const context: SignupApplicationEntity = {
        ...applicationTrackingContext,
        ...updatedApplicationContext,
      };

      // Ensures that the context is not updated unnecessarily causing a re-render
      if (isEqual(context, applicationTrackingContext)) {
        return applicationTrackingContext;
      }

      setApplicationTrackingContext(updatedApplicationContext);

      return updatedApplicationContext;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setApplicationTrackingContext, userInformation, isHostedLink]
  );

  return { updateApplicationContext };
};

export { useApplicationEntityUpdate };
